import * as Fingerprint from "@fingerprint/agent";
import { FINGERPRINT_EVENT_HEADER } from "@repo/common";
import { env } from "@repo/env";

import { isDeviceIdentificationRoute } from "./risk-event-routes";
import { rememberFingerprintEvent } from "./risk-signal-cookies";

/**
 * Identification may wait this long before the request goes out without it.
 * Fingerprint advises against much less: slow devices fail identification.
 */
const IDENTIFY_TIMEOUT_MS = 3_000;

let identify: (() => Promise<string | null>) | null = null;

/**
 * Starts the Fingerprint agent and stamps a fresh identification event id onto
 * every risk-event and sign-up request, for the server to look the device up
 * with. Each id is also kept in a short-lived cookie, which is how a risk
 * event recorded on the navigation that follows (an OAuth callback, a
 * magic-link click, the apply entry render) names the device.
 *
 * Off without a public key. Not gated on cookie consent: it identifies only
 * around sign-up, apply entry, phone verification and cash-out, for fraud
 * prevention, which the cookie policy lists as strictly necessary alongside
 * Stripe's fraud cookies. Identification failing or timing out sends the
 * request without the header rather than failing it.
 */
export function startFingerprint(): void {
  const apiKey = env.NEXT_PUBLIC_FINGERPRINT_PUBLIC_KEY;
  if (!apiKey) {
    return;
  }
  const agent = Fingerprint.start({
    apiKey,
    region: env.NEXT_PUBLIC_FINGERPRINT_REGION,
    endpoints: env.NEXT_PUBLIC_FINGERPRINT_ENDPOINT,
  });
  const identifyDevice = async (): Promise<string | null> => {
    try {
      const { event_id } = await agent.get({ timeout: IDENTIFY_TIMEOUT_MS });
      rememberFingerprintEvent(event_id);
      return event_id;
    } catch {
      // Unidentified: the risk event records no device.
      return null;
    }
  };
  identify = identifyDevice;
  const nextFetch = window.fetch;
  window.fetch = async (input, init) => {
    const url = new URL(
      input instanceof Request ? input.url : input,
      location.href,
    );
    const method =
      init?.method ?? (input instanceof Request ? input.method : "GET");
    if (
      url.origin !== location.origin ||
      !isDeviceIdentificationRoute(url.pathname, method)
    ) {
      return nextFetch(input, init);
    }
    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    const eventId = await identifyDevice();
    if (eventId) {
      headers.set(FINGERPRINT_EVENT_HEADER, eventId);
    }
    return nextFetch(input, { ...init, headers });
  };
}

/**
 * Identifies the device ahead of a navigation that records a risk event, such
 * as entering an application from its brief. Never waits on the caller.
 */
export function primeDeviceIdentification(): void {
  void identify?.();
}
